import React, {useState} from 'react';

import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {BoltzGenFormContent} from 'model-hub/scientific-models/input-forms/boltzgen/BoltzGenInputForm';
import {SCIENTIFIC_MODEL} from 'model-hub/scientific-models/ScientificModelConstants';
import {useAvailableGenerativeModels} from 'model-hub/scientific-models/useAvailableScientificModels';
import * as logger from 'util/Logging';
import {assertNever} from 'util/Types';

import GenerativeModelRunDetails from './GenerativeModelRunDetails';
import {BoltzGenTargetType} from './GenerativeModelsConstants';
import {useGetGenerativeModelRuns, GenerativeModelsRunsList} from './GenerativeModelsRunsList';
import {type NormalizedRun} from './GenerativeModelsRunsList';

enum GenerativeModelsViewType {
  FORM = 'FORM',
  RUNS_LIST = 'RUNS_LIST',
  RUN_DETAILS = 'RUN_DETAILS',
}

interface GenerativeModelsViewProps {
  currentEntityType: BoltzGenTargetType;
  targetId: string;
}

export default function GenerativeModelsView({
  currentEntityType,
  targetId,
}: GenerativeModelsViewProps): JSX.Element | null {
  const [currentView, setCurrentView] = useState<GenerativeModelsViewType>(
    GenerativeModelsViewType.RUNS_LIST
  );
  const [selectedRun, setSelectedRun] = useState<NormalizedRun | null>(null);

  const availableGenerativeModels = useAvailableGenerativeModels();
  const isBoltzGenInstalled =
    availableGenerativeModels != null && availableGenerativeModels.includes(SCIENTIFIC_MODEL.BOLTZGEN);

  const {pendingRuns, completedRuns, loading, error, refetch} = useGetGenerativeModelRuns({
    currentEntityType,
    targetId,
  });

  function getDisabledMessage(): string | undefined {
    if (availableGenerativeModels == null) {
      return undefined;
    }
    // Permission gating takes precedence over installation state: an AVAILABLE_DISABLED tenant
    // should always see the permission message, regardless of whether BoltzGen is installed.
    const permissionMessage = getScientificAIDisabledMessage();
    if (permissionMessage != null) {
      return permissionMessage;
    }
    if (!isBoltzGenInstalled) {
      return 'BoltzGen is not installed on this tenant. Ask your tenant admin to install it from the Scientific Models page.';
    }
    return undefined;
  }

  function renderListView(): JSX.Element {
    if (error) {
      return <div>Failed to load runs</div>;
    }
    return (
      <GenerativeModelsRunsList
        loading={loading}
        pendingRuns={pendingRuns}
        onRunSelected={(run: NormalizedRun) => {
          setSelectedRun(run);
          setCurrentView(GenerativeModelsViewType.RUN_DETAILS);
        }}
        completedRuns={completedRuns}
        onCreateNewRun={() => {
          setCurrentView(GenerativeModelsViewType.FORM);
        }}
        disabledMessage={getDisabledMessage()}
      />
    );
  }

  switch (currentView) {
    case GenerativeModelsViewType.FORM:
      return (
        <BoltzGenFormContent
          targetEntity={{entityType: currentEntityType, entityId: targetId}}
          navigateToList={() => {
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
          onSubmit={() => {
            refetch();
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
          source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
          disabledMessage={getDisabledMessage()}
        />
      );
    case GenerativeModelsViewType.RUNS_LIST:
      return renderListView();
    case GenerativeModelsViewType.RUN_DETAILS:
      if (!selectedRun) {
        logger.warn('No selected run when switching to run details view');
        return renderListView();
      }
      return (
        <GenerativeModelRunDetails
          run={selectedRun}
          navigateToList={() => {
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
        />
      );
    default:
      assertNever(currentView);
      return null;
  }
}
